import createClient, { type Client } from 'openapi-fetch'

import { apiBaseURL } from 'utils/axios'

export interface ExternalReviewGeneratedClientConfig {
  baseUrl?: string
  fetchImpl?: typeof fetch
}

export interface OpenApiResponse<TData, TError = unknown> {
  data?: TData
  error?: TError
  response: Response
}

export class ExternalReviewApiError extends Error {
  status: number
  body: string
  response: {
    status: number
    data: unknown
  }

  constructor(status: number, body: string, data: unknown) {
    const bodySnippet = body.trim().slice(0, 300)
    super(
      bodySnippet
        ? `External Review API request failed with status ${status}: ${bodySnippet}`
        : `External Review API request failed with status ${status}`
    )
    this.name = 'ExternalReviewApiError'
    this.status = status
    this.body = body
    this.response = {
      status,
      data,
    }
  }
}

export class ExternalReviewNetworkError extends Error {
  readonly method: string
  readonly url: string
  readonly endpoint: string
  readonly originalErrorName: string
  readonly originalErrorMessage: string
  readonly cause: unknown

  constructor(request: Request, cause: unknown) {
    const originalError =
      cause instanceof Error ? cause : new Error(String(cause))
    const endpoint = normalizeEndpoint(request.url)

    super(
      `External Review API network request failed: ${request.method} ${endpoint}: ${originalError.message}`
    )
    this.name = 'ExternalReviewNetworkError'
    this.method = request.method
    this.url = request.url
    this.endpoint = endpoint
    this.originalErrorName = originalError.name
    this.originalErrorMessage = originalError.message
    this.cause = cause
  }
}

export class ExternalReviewGeneratedClient<Paths extends object> {
  readonly client: Client<Paths>

  constructor({
    baseUrl = apiBaseURL,
    fetchImpl = getDefaultFetch(),
  }: ExternalReviewGeneratedClientConfig = {}) {
    this.client = createClient<Paths>({
      baseUrl: getBaseUrl(baseUrl),
      fetch: async (request) => {
        const preparedRequest = withJsonAccept(request)

        try {
          return await fetchImpl(preparedRequest)
        } catch (error) {
          throw new ExternalReviewNetworkError(preparedRequest, error)
        }
      },
    })
  }

  async unwrap<TData, TError = unknown>(
    result: OpenApiResponse<TData, TError>
  ): Promise<TData> {
    if (!result.response.ok) {
      throw new ExternalReviewApiError(
        result.response.status,
        stringifyOpenApiError(result.error),
        result.error
      )
    }

    if (result.data === undefined || result.data === null) {
      throw new ExternalReviewApiError(
        result.response.status,
        'Empty response body',
        result.data
      )
    }

    return result.data
  }
}

function getBaseUrl(baseUrl: string | undefined): string {
  return baseUrl?.replace(/\/+$/, '') ?? ''
}

function normalizeEndpoint(url: string): string {
  const pathname = new URL(url, 'https://external-review.invalid').pathname

  return pathname
    .replace(
      /\/[0-9a-f]{8}-[0-9a-f]{4}-[1-5][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}(?=\/|$)/gi,
      '/{uuid}'
    )
    .replace(/\/[0-9a-f]{24}(?=\/|$)/gi, '/{objectId}')
}

function getDefaultFetch(): typeof fetch {
  if (globalThis.fetch) {
    return globalThis.fetch.bind(globalThis)
  }

  return missingFetch
}

const missingFetch: typeof fetch = async () => {
  throw new ExternalReviewApiError(
    0,
    'global fetch is not available',
    undefined
  )
}

function withJsonAccept(request: Request): Request {
  const headers = new Headers(request.headers)
  headers.set('Accept', 'application/json')

  return new Request(request, { headers })
}

function stringifyOpenApiError(error: unknown): string {
  if (error === undefined || error === null) {
    return '{}'
  }

  if (typeof error === 'string') {
    return error
  }

  return JSON.stringify(error)
}
